import React, { useState, useRef, useEffect } from 'react';
import '@/App.css';
import axios from 'axios';
import SignatureCanvas from 'react-signature-canvas';
import { Toaster, toast } from 'sonner';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import html2pdf from 'html2pdf.js';

// Empty REACT_APP_BACKEND_URL means same-origin ("/api"), so the site works on
// every domain that points at it (nra-debit.tcauditors.co.za and debitorder.newredruthacademy.co.za)
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// List of South African Banks with Universal Branch Codes
const SOUTH_AFRICAN_BANKS = [
  { value: 'absa', label: 'ABSA Bank', branchCode: '632005' },
  { value: 'african-bank', label: 'African Bank', branchCode: '430000' },
  { value: 'barclays-bank', label: 'Barclays Bank', branchCode: '590000' },
  { value: 'bidvest-bank', label: 'Bidvest Bank', branchCode: '462005' },
  { value: 'capitec', label: 'Capitec Bank', branchCode: '470010' },
  { value: 'capitec-business', label: 'Capitec Business Bank', branchCode: '450105' },
  { value: 'discovery-bank', label: 'Discovery Bank', branchCode: '679000' },
  { value: 'firstrand-bank', label: 'FirstRand Bank Ltd', branchCode: '201419' },
  { value: 'fnb', label: 'First National Bank (FNB)', branchCode: '250655' },
  { value: 'grindrod-bank', label: 'Grindrod Bank', branchCode: '584000' },
  { value: 'hollard-bank', label: 'Hollard Bank', branchCode: '585001' },
  { value: 'hsbc-bank', label: 'HSBC Bank', branchCode: '587000' },
  { value: 'investec', label: 'Investec Bank', branchCode: '580105' },
  { value: 'mercantile-bank', label: 'Mercantile Bank (now Capitec Business Bank)', branchCode: '450105' },
  { value: 'nedbank', label: 'Nedbank', branchCode: '198765' },
  { value: 'old-mutual-bank', label: 'Old Mutual Bank', branchCode: '462005' },
  { value: 'standard-bank', label: 'Standard Bank', branchCode: '051001' },
  { value: 'standard-chartered', label: 'Standard Chartered Bank', branchCode: '730020' },
  { value: 'tymebank', label: 'TymeBank', branchCode: '678910' },
  { value: 'bank-zero', label: 'Bank Zero', branchCode: '888000' },
  { value: 'sasfin-bank', label: 'Sasfin Bank', branchCode: '683000' },
  { value: 'bank-of-athens', label: 'South African Bank of Athens Limited', branchCode: '410506' },
  { value: 'albaraka-bank', label: 'Albaraka Bank', branchCode: '800000' },
  { value: 'habib-bank', label: 'Habib Overseas Bank', branchCode: '170102' },
  { value: 'hbz-bank', label: 'HBZ Bank', branchCode: '570105' },
  { value: 'ithala-bank', label: 'Ithala Bank', branchCode: '420000' },
  { value: 'postbank', label: 'PostBank (South African Post Office)', branchCode: '460005' },
  { value: 'rand-merchant-bank', label: 'Rand Merchant Bank', branchCode: '261251' },
  { value: 'rmbpb', label: 'RMB Private Bank', branchCode: '222026' },
  { value: 'ubank', label: 'Ubank', branchCode: '431010' },
].sort((a, b) => a.label.localeCompare(b.label));

// Per-bank account number validation rules (keyed by exact bank label above)
const BANK_ACCOUNT_RULES = {
  'ABSA Bank': { minLength: 10, maxLength: 11, prefixes: ['1', '4', '9'], prefixRequired: false, name: 'ABSA Bank', description: '10 or 11 digits', note: 'Accounts are left-padded with zeros to 11 digits for processing.' },
  'First National Bank (FNB)': { minLength: 11, maxLength: 11, prefixes: ['5', '6', '7'], prefixRequired: true, name: 'FNB', description: '11 digits', note: 'Cheque accounts start with 6; Savings with 7 or 5.' },
  'Nedbank': { minLength: 10, maxLength: 10, prefixes: ['1'], prefixRequired: true, name: 'Nedbank', description: '10 digits', note: 'Please enter your Account Number, not your Profile Number.' },
  'Standard Bank': { minLength: 9, maxLength: 11, prefixes: ['0', '1', '2', '3', '4'], prefixRequired: false, name: 'Standard Bank', description: '9 to 11 digits', note: '9-digit accounts are valid and will be left-padded with zeros.' },
  'Capitec Bank': { minLength: 10, maxLength: 10, prefixes: ['1', '2'], prefixRequired: true, name: 'Capitec Bank', description: '10 digits', note: 'Savings accounts start with 1; Loan/Bond accounts start with 2.' },
  'Capitec Business Bank': { minLength: 10, maxLength: 10, prefixes: ['1', '2'], prefixRequired: false, name: 'Capitec Business Bank', description: '10 digits' },
  'African Bank': { minLength: 11, maxLength: 11, name: 'African Bank', description: '11 digits' },
  'Ubank': { minLength: 11, maxLength: 11, name: 'Ubank', description: '11 digits', note: 'Formerly Teba Bank.' },
  'Discovery Bank': { minLength: 11, maxLength: 11, rejectLength: 16, name: 'Discovery Bank', description: '11 digits', note: 'Please enter your account number, not your 16-digit card number.' },
  'TymeBank': { minLength: 11, maxLength: 11, name: 'TymeBank', description: '11 digits' },
  'Bank Zero': { minLength: 10, maxLength: 11, name: 'Bank Zero', description: '10 or 11 digits' },
  'Bidvest Bank': { minLength: 11, maxLength: 11, name: 'Bidvest Bank', description: '11 digits' },
  'Sasfin Bank': { minLength: 11, maxLength: 11, name: 'Sasfin Bank', description: '11 digits' },
  'Investec Bank': { minLength: 11, maxLength: 11, prefixes: ['1', '5'], prefixRequired: false, name: 'Investec Bank', description: '11 digits', note: 'Private Bank accounts often start with 1; Corporate with 5.' },
  'South African Bank of Athens Limited': { minLength: 11, maxLength: 11, name: 'Bank of Athens', description: '11 digits', note: 'Formerly Access Bank South Africa.' },
  'Albaraka Bank': { minLength: 11, maxLength: 11, name: 'Albaraka Bank', description: '11 digits', note: 'Islamic Banking. Do not enter international IBAN format.' },
  'Grindrod Bank': { minLength: 11, maxLength: 11, name: 'Grindrod Bank', description: '11 digits' },
  'Habib Overseas Bank': { minLength: 11, maxLength: 11, name: 'Habib Overseas Bank', description: '11 digits' },
  'HBZ Bank': { minLength: 11, maxLength: 11, name: 'HBZ Bank', description: '11 digits' },
  'Ithala Bank': { minLength: 11, maxLength: 11, name: 'Ithala Bank', description: '11 digits' },
  'Mercantile Bank (now Capitec Business Bank)': { minLength: 11, maxLength: 11, name: 'Mercantile Bank', description: '11 digits' },
  'PostBank (South African Post Office)': { minLength: 11, maxLength: 11, name: 'PostBank', description: '11 digits' },
  'Rand Merchant Bank': { minLength: 11, maxLength: 11, prefixes: ['5', '6', '7'], prefixRequired: false, name: 'Rand Merchant Bank', description: '11 digits', note: 'Uses same validation rules as FNB.' },
  'RMB Private Bank': { minLength: 11, maxLength: 11, prefixes: ['5', '6', '7'], prefixRequired: false, name: 'RMB Private Bank', description: '11 digits', note: 'Uses same validation rules as FNB.' },
  'Barclays Bank': { minLength: 10, maxLength: 11, name: 'Barclays Bank', description: '10 or 11 digits' },
  'FirstRand Bank Ltd': { minLength: 11, maxLength: 11, prefixes: ['5', '6', '7'], prefixRequired: false, name: 'FirstRand', description: '11 digits', note: 'Parent company of FNB - uses same validation rules.' },
  'Hollard Bank': { minLength: 10, maxLength: 11, name: 'Hollard Bank', description: '10 or 11 digits' },
  'HSBC Bank': { minLength: 10, maxLength: 11, name: 'HSBC Bank', description: '10 or 11 digits' },
  'Old Mutual Bank': { minLength: 10, maxLength: 11, name: 'Old Mutual Bank', description: '10 or 11 digits' },
  'Standard Chartered Bank': { minLength: 10, maxLength: 11, name: 'Standard Chartered Bank', description: '10 or 11 digits' },
};

// Format a list of prefixes as "5, 6, or 7"
const formatPrefixList = (prefixes) =>
  prefixes.length > 1
    ? `${prefixes.slice(0, -1).join(', ')}, or ${prefixes[prefixes.length - 1]}`
    : prefixes[0];


function App() {
  const [formData, setFormData] = useState({
    account_holder_name: '',
    id_number: '',
    physical_address: '',
    bank_name: '',
    branch_name: '',
    branch_code: '',
    account_number: '',
    account_type: 'Current',
    signing_date: new Date().toISOString().split('T')[0],
    contact_number: '',
    email: '',
    amount: 'Invoice amount - variable amounts allowed',
    contract_reference: '',
    company_name: '',
    company_registration_number: '',
    company_contact_name: '',
    company_contact_email: '',
    company_contact_phone: '',
  });

  const [accountHolderType, setAccountHolderType] = useState(''); // '' | 'company' | 'individual'
  const [companyAuthChecked, setCompanyAuthChecked] = useState(false);

  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailCredentials, setEmailCredentials] = useState({
    gmail_email: '',
    gmail_password: '',
  });
  const [savedFormId, setSavedFormId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [openBankCombobox, setOpenBankCombobox] = useState(false);
  const [contactNumberError, setContactNumberError] = useState('');
  const [contractReferenceError, setContractReferenceError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [companyContactPhoneError, setCompanyContactPhoneError] = useState('');
  const [companyContactEmailError, setCompanyContactEmailError] = useState('');
  const [physicalAddressError, setPhysicalAddressError] = useState('');
  const [idNumberError, setIdNumberError] = useState('');
  const [accountNumberWarning, setAccountNumberWarning] = useState('');
  const [showSavingModal, setShowSavingModal] = useState(false);
  const [showAddressTooltip, setShowAddressTooltip] = useState(false);
  const [acknowledgements, setAcknowledgements] = useState({
    agreement1: false,
    agreement2: false,
    agreement3: false,
    agreement4: false,
    agreement5: false,
  });

  const signatureRef = useRef(null);

  // Block browser printing (Ctrl/Cmd+P, right-click print, browser menu print).
  // The internal html2pdf.js flow is unaffected — it renders via html2canvas
  // using screen media, not the browser's print pipeline.
  useEffect(() => {
    const blockPrintShortcut = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === 'p' || e.key === 'P')) {
        e.preventDefault();
        e.stopPropagation();
        toast.error('Printing is disabled. Please complete and submit the form digitally.');
        return false;
      }
    };
    const handleBeforePrint = () => {
      toast.error('Printing is disabled. Please complete and submit the form digitally.');
    };
    window.addEventListener('keydown', blockPrintShortcut);
    window.addEventListener('beforeprint', handleBeforePrint);
    return () => {
      window.removeEventListener('keydown', blockPrintShortcut);
      window.removeEventListener('beforeprint', handleBeforePrint);
    };
  }, []);

  // Scroll to top on component mount and disable browser scroll restoration
  useEffect(() => {
    // Disable browser's automatic scroll restoration
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    
    // Scroll to top immediately
    window.scrollTo(0, 0);
    
    // Also scroll to top after a brief delay to override any browser behavior
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
  }, []);

  const validateEmail = (value) => {
    if (!value) {
      return '';
    }
    
    const email = value.trim().toLowerCase();
    
    // Basic email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address';
    }
    
    // Common typo domains
    const commonDomains = {
      'gmial.com': 'gmail.com',
      'gmai.com': 'gmail.com',
      'gmail.co': 'gmail.com',
      'gnail.com': 'gmail.com',
      'hotmial.com': 'hotmail.com',
      'hotmail.co': 'hotmail.com',
      'yahooo.com': 'yahoo.com',
      'yaho.com': 'yahoo.com',
      'yahoo.co': 'yahoo.com',
      'outlok.com': 'outlook.com',
      'outllook.com': 'outlook.com',
      'outlook.co': 'outlook.com',
    };
    
    const domain = email.split('@')[1];
    if (commonDomains[domain]) {
      return `Did you mean ${email.split('@')[0]}@${commonDomains[domain]}?`;
    }
    
    return '';
  };

  const validateIdNumber = (idNumber) => {
    if (!idNumber) {
      return '';
    }

    // Remove any spaces or non-numeric characters
    const cleanId = idNumber.replace(/\D/g, '');

    // 1. Check if exactly 13 digits
    if (cleanId.length !== 13) {
      return 'South African ID number must be exactly 13 digits';
    }

    // 2. Validate date of birth (positions 1-6: YYMMDD)
    const year = cleanId.substring(0, 2);
    const month = cleanId.substring(2, 4);
    const day = cleanId.substring(4, 6);

    // Determine century (00-24 = 2000s, 25-99 = 1900s)
    const currentYear = new Date().getFullYear() % 100;
    const fullYear = parseInt(year) <= currentYear + 10 ? 2000 + parseInt(year) : 1900 + parseInt(year);

    // Validate month (01-12)
    const monthNum = parseInt(month);
    if (monthNum < 1 || monthNum > 12) {
      return 'Invalid birth month in ID number';
    }

    // Validate day (01-31, considering month)
    const dayNum = parseInt(day);
    const daysInMonth = new Date(fullYear, monthNum, 0).getDate();
    if (dayNum < 1 || dayNum > daysInMonth) {
      return 'Invalid birth day in ID number';
    }

    // Check if date is not in the future
    const birthDate = new Date(fullYear, monthNum - 1, dayNum);
    if (birthDate > new Date()) {
      return 'Birth date cannot be in the future';
    }

    // 3. Validate gender sequence (positions 7-10: SSSS)
    const genderSeq = parseInt(cleanId.substring(6, 10));
    if (isNaN(genderSeq) || genderSeq < 0 || genderSeq > 9999) {
      return 'Invalid gender sequence in ID number';
    }
    // 0000-4999 = Female, 5000-9999 = Male

    // 4. Validate citizenship indicator (position 11)
    const citizenship = cleanId.charAt(10);
    if (!['0', '1', '2'].includes(citizenship)) {
      return 'Invalid citizenship indicator in ID number';
    }

    // 5. Validate position 12 (usually 8 or 9, but can be other digits)
    const positionA = cleanId.charAt(11);
    if (!/^\d$/.test(positionA)) {
      return 'Invalid ID number format';
    }

    // 6. Validate Luhn checksum (position 13)
    const checksumDigit = parseInt(cleanId.charAt(12));
    
    // Luhn algorithm implementation for SA ID
    // Process first 12 digits, doubling every second digit from the RIGHT
    let sum = 0;
    
    // Parity: since payload length is 12 (even), double digits at even positions (2,4,6,8,10,12 from left)
    for (let i = 0; i < 12; i++) {
      let digit = parseInt(cleanId.charAt(i));
      
      // Double every second digit starting from position 2 (index 1)
      // Positions 2,4,6,8,10,12 from left = indices 1,3,5,7,9,11
      if (i % 2 === 1) {
        digit *= 2;
        if (digit > 9) {
          digit -= 9; // Subtract 9 instead of adding digits (equivalent)
        }
      }
      
      sum += digit;
    }
    
    // Calculate what the checksum should be
    const calculatedChecksum = (10 - (sum % 10)) % 10;
    
    if (calculatedChecksum !== checksumDigit) {
      return 'ID number checksum validation failed. Please verify that the number is correct.';
    }

    return '';
  };

  // Generic South African cellphone number validator, reused by every phone field
  // (Contact Number and the optional Company Contact Person phone) so the same rules apply everywhere.
  const validatePhoneNumber = (value, fieldLabel = 'Contact number') => {
    // Remove any spaces or dashes for validation
    const cleanedValue = value.replace(/[\s-]+/g, '');

    if (!cleanedValue) {
      return '';
    }

    if (!/^\d+$/.test(cleanedValue)) {
      return `${fieldLabel} must contain only digits`;
    }

    if (!cleanedValue.startsWith('0')) {
      return `${fieldLabel} must start with 0`;
    }

    if (cleanedValue.length !== 10) {
      return `${fieldLabel} must be exactly 10 digits`;
    }

    return '';
  };

  // Requires at least 3 populated lines (per the Address Format Guide tooltip) so the
  // address renders neatly across separate lines in the generated PDF instead of one long line.
  const validatePhysicalAddress = (value) => {
    if (!value || !value.trim()) {
      return '';
    }

    const nonEmptyLines = value.split('\n').map((line) => line.trim()).filter(Boolean);

    if (nonEmptyLines.length < 3) {
      return 'This address looks incomplete and does not contain all the elements recommended by the Address Format Guide. Please follow the tooltip below and enter each part of the address (e.g. street, suburb/city, postal code) on its own line.';
    }

    return '';
  };

  // ---- Company Name: live casing, suffix auto-append, CIPC character validation ----

  // CIPC allows letters, numbers, spaces and parentheses only.
  const SPECIAL_RE = /[^a-zA-Z0-9 ()]/;
  const NOT_ALLOWED = '! @ # $ % ^ & * _ + = { } [ ] | \\ / : ; " \' < > ? , . ~ `';
  const hasSpecial = (v) => SPECIAL_RE.test(v);

  // A) Live casing formatter - runs on every keystroke.
  const formatCompanyName = (str) =>
    str
      .replace(/^\s+/, '') // never allow leading spaces
      .split(/(\s+)/) // keep whitespace tokens so spacing is preserved while typing
      .map((token) => {
        if (token.trim() === '') return token;
        const suffixMatch = token.match(/^(\(?)(pty|ltd)(\)?)$/i);
        if (suffixMatch) {
          const [, open, word, close] = suffixMatch;
          return open + word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() + close;
        }
        if (token.length <= 3) return token.toUpperCase();
        return token.charAt(0).toUpperCase() + token.slice(1).toLowerCase();
      })
      .join('');

  // B) Suffix normaliser - runs on blur only.
  // "Ltd" (public company) and "CC" (Close Corporation) are also valid standalone
  // designations - only names ending with none of these get "(Pty) Ltd" appended.
  const FULL_SUFFIX_RE = /(\(pty\)\s*ltd|pty\s*ltd|(?:proprietary|prop)\s+(?:limited|ltd)|\bltd\b|\bcc\b)\s*$/i;
  const TRAILING_SUFFIX_RE = /\s*(?:\(\s*(?:pty|pt|p)?\)?\s*(?:ltd|lt|l)?|pty\s*(?:ltd|lt|l)?|(?:proprietary|prop)\s*(?:limited|limite|limit|limi|lim|li|ltd|lt|l)?)\s*$/i;
  // Nobody types "Close Corporation" in full at the end of a name - if they do, replace it with "CC".
  const CLOSE_CORPORATION_RE = /\bclose\s+corporation\s*$/i;

  // CIPC company registration number format: YYYY/NNNNNN/NN (e.g. 2015/123456/07)
  const CIPC_REG_NUMBER_RE = /^\d{4}\/\d{6}\/\d{2}$/;
  // '' while blank (grey hint), 'invalid' while typed but not matching (red), 'valid' once it matches (green)
  const companyRegNumberStatus = !formData.company_registration_number.trim()
    ? ''
    : CIPC_REG_NUMBER_RE.test(formData.company_registration_number.trim()) ? 'valid' : 'invalid';

  const normalizeCompanySuffix = (name) => {
    let trimmed = (name || '').trim();
    if (!trimmed) return trimmed;
    if (CLOSE_CORPORATION_RE.test(trimmed)) {
      trimmed = trimmed.replace(CLOSE_CORPORATION_RE, 'CC').trim();
    }
    if (FULL_SUFFIX_RE.test(trimmed)) return trimmed;
    const base = trimmed.replace(TRAILING_SUFFIX_RE, '').trim();
    return `${base || trimmed} (Pty) Ltd`;
  };

  const validateAccountNumber = (accountNumber, bankName) => {
    if (!accountNumber || !bankName) {
      return '';
    }
    
    // Remove any spaces or non-numeric characters
    const cleanNumber = accountNumber.replace(/\D/g, '');
    
    // === PATTERN VALIDATION CHECKS (DETECT SUSPICIOUS NUMBERS) ===
    
    // 1. Check for all same digits (e.g., 1111111111, 9999999999)
    if (/^(.)\1+$/.test(cleanNumber)) {
      return 'Account number appears invalid. Please enter a valid bank account number.';
    }
    
    // 2. Check for all zeros
    if (/^0+$/.test(cleanNumber)) {
      return 'Account number appears invalid. Please enter a valid bank account number.';
    }
    
    // 3. Check for sequential ascending numbers (e.g., 123456789, 234567890)
    const hasAscendingSequence = (str) => {
      for (let i = 0; i < str.length - 5; i++) {
        let isSequential = true;
        for (let j = 0; j < 6; j++) {
          if (parseInt(str[i + j]) !== (parseInt(str[i]) + j) % 10) {
            isSequential = false;
            break;
          }
        }
        if (isSequential) return true;
      }
      return false;
    };
    
    if (hasAscendingSequence(cleanNumber)) {
      return 'Account number appears invalid. Sequential numbers are not permitted.';
    }
    
    // 4. Check for sequential descending numbers (e.g., 987654321)
    const hasDescendingSequence = (str) => {
      for (let i = 0; i < str.length - 5; i++) {
        let isSequential = true;
        for (let j = 0; j < 6; j++) {
          const expected = (parseInt(str[i]) - j + 10) % 10;
          if (parseInt(str[i + j]) !== expected) {
            isSequential = false;
            break;
          }
        }
        if (isSequential) return true;
      }
      return false;
    };
    
    if (hasDescendingSequence(cleanNumber)) {
      return 'Account number appears invalid. Sequential numbers are not permitted.';
    }
    
    // 5. Check for four or more consecutive identical digits (e.g., 1234444567)
    if (/(\d)\1{3,}/.test(cleanNumber)) {
      return 'Account number appears unusual. Contains four or more consecutive identical digits. Please verify before proceeding.';
    }
    
    // 6. Check for simple alternating patterns (e.g., 121212, 343434)
    const hasAlternatingPattern = (str) => {
      if (str.length < 6) return false;
      const pattern = str.substring(0, 2);
      const repeated = pattern.repeat(Math.ceil(str.length / 2)).substring(0, str.length);
      return str === repeated;
    };
    
    if (hasAlternatingPattern(cleanNumber)) {
      return 'Account number appears invalid. Repetitive patterns are not permitted.';
    }
    
    // 7. Check for insufficient digit variety (at least 3 different digits required)
    const uniqueDigits = new Set(cleanNumber.split('')).size;
    if (uniqueDigits < 3) {
      return 'Account number appears invalid. Please enter a valid bank account number with varied digits.';
    }
    
    // === BANK-SPECIFIC VALIDATION (length, prefix, special rejections) ===
    const validation = BANK_ACCOUNT_RULES[bankName];

    if (validation) {
      // Special check: reject 16-digit card numbers (e.g. Discovery Bank)
      if (validation.rejectLength && cleanNumber.length === validation.rejectLength) {
        return `Please enter your ${validation.name} account number, not your 16-digit card number.`;
      }

      // Length validation
      if (validation.minLength === validation.maxLength) {
        if (cleanNumber.length !== validation.minLength) {
          return `${validation.name} account numbers must be exactly ${validation.minLength} digits.`;
        }
      } else if (cleanNumber.length < validation.minLength || cleanNumber.length > validation.maxLength) {
        return `${validation.name} account numbers must be between ${validation.minLength} and ${validation.maxLength} digits.`;
      }

      // Prefix validation
      if (validation.prefixes) {
        const firstDigit = cleanNumber.charAt(0);
        if (!validation.prefixes.includes(firstDigit)) {
          const prefixText = formatPrefixList(validation.prefixes);
          if (validation.prefixRequired) {
            return `${validation.name} account numbers must start with ${prefixText}.`;
          }
          return `${validation.name} account numbers typically start with ${prefixText}. Please verify your number is correct before proceeding.`;
        }
      }
    } else {
      // Fallback for any bank not in the rules map: SA standard range is 9-11 digits
      if (cleanNumber.length < 9 || cleanNumber.length > 11) {
        return `South African bank account numbers are typically 9-11 digits. You entered ${cleanNumber.length}. Please verify your number is correct.`;
      }
    }

    return '';
  };

  const validateContractReference = (value) => {
    if (!value) {
      return '';
    }
    
    if (value.length !== 6) {
      return 'Contract reference must be exactly 6 characters';
    }
    
    const firstThree = value.substring(0, 3);
    const lastThree = value.substring(3, 6);
    
    // Check first 3 characters are alphabetic
    if (!/^[A-Za-z]{3}$/.test(firstThree)) {
      return 'First 3 characters must be letters';
    }
    
    // Check last 3 characters are numeric
    if (!/^\d{3}$/.test(lastThree)) {
      return 'Last 3 characters must be numbers';
    }

    return '';
  };

  // Contract reference = first 3 non-space characters of the source name + "001".
  // Source is the Company Name for a company account, else the account holder's name.
  // Remove ALL whitespace first so names like "JP SONNEBERG" produce "JPS001" (not "JP 001").
  const buildContractReference = (sourceName) => {
    const nameNoSpaces = (sourceName || '').replace(/\s+/g, '');
    if (nameNoSpaces.length < 3) {
      setContractReferenceError('');
      return '';
    }
    const contractRef = `${nameNoSpaces.substring(0, 3).toUpperCase()}001`;
    setContractReferenceError(validateContractReference(contractRef));
    return contractRef;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Validate email on change
    if (name === 'email') {
      const error = validateEmail(value);
      setEmailError(error);
    }
    
    // Validate contact number on change
    if (name === 'contact_number') {
      const error = validatePhoneNumber(value, 'Contact number');
      setContactNumberError(error);
    }

    // Validate optional company contact person phone/email on change (same rules as above)
    if (name === 'company_contact_phone') {
      const error = validatePhoneNumber(value, 'Contact person phone');
      setCompanyContactPhoneError(error);
    }

    if (name === 'company_contact_email') {
      const error = validateEmail(value);
      setCompanyContactEmailError(error);
    }
    
    // Validate ID number on change
    if (name === 'id_number') {
      const error = validateIdNumber(value);
      setIdNumberError(error);
    }

    // Validate physical address on change (must contain at least 3 populated lines)
    if (name === 'physical_address') {
      const error = validatePhysicalAddress(value);
      setPhysicalAddressError(error);
    }
    
    // Validate account number on change
    if (name === 'account_number') {
      const warning = validateAccountNumber(value, formData.bank_name);
      setAccountNumberWarning(warning);
    }
    
    // Validate contract reference on change
    if (name === 'contract_reference') {
      // Limit to 6 characters
      if (value.length > 6) {
        return;
      }
      const error = validateContractReference(value);
      setContractReferenceError(error);
      
      setFormData((prev) => ({
        ...prev,
        [name]: value.toUpperCase(),
      }));
      return;
    }
    
    // Auto-generate contract reference from the account holder's name (natural person accounts only;
    // for company accounts it comes from the Company Name below)
    if (name === 'account_holder_name') {
      if (accountHolderType === 'company') {
        setFormData((prev) => ({ ...prev, [name]: value }));
        return;
      }
      const contractRef = buildContractReference(value);
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        contract_reference: contractRef,
      }));
      return;
    }

    // Live-format Company Name as the user types (title-case, acronym-preserving casing)
    // and auto-generate the contract reference from it
    if (name === 'company_name') {
      const formatted = formatCompanyName(value);
      const contractRef = buildContractReference(formatted);
      setFormData((prev) => ({
        ...prev,
        [name]: formatted,
        contract_reference: contractRef,
      }));
      return;
    }

    // Company Registration Number: digits and "/" only (no letters or spaces), max 14 chars (YYYY/NNNNNN/NN)
    if (name === 'company_registration_number') {
      setFormData((prev) => ({
        ...prev,
        [name]: value.replace(/[^0-9/]/g, '').slice(0, 14),
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Auto-appends/normalises the "(Pty) Ltd" suffix once the client leaves the Company Name field
  const handleCompanyNameBlur = () => {
    setFormData((prev) => ({
      ...prev,
      company_name: normalizeCompanySuffix(prev.company_name),
    }));
  };

  const clearSignature = () => {
    signatureRef.current?.clear();
  };

  const handleAcknowledgementChange = (agreementKey) => {
    setAcknowledgements((prev) => ({
      ...prev,
      [agreementKey]: !prev[agreementKey],
    }));
  };

  const handleAccountHolderTypeChange = (type) => {
    setAccountHolderType(type);
    // Reset company-only fields when switching away from Company
    if (type !== 'company') {
      setCompanyAuthChecked(false);
      setFormData((prev) => ({
        ...prev,
        company_name: '',
        company_registration_number: '',
        company_contact_name: '',
        company_contact_email: '',
        company_contact_phone: '',
      }));
      setCompanyContactPhoneError('');
      setCompanyContactEmailError('');
    }
    // Re-derive the contract reference from whichever name now applies
    // (Company Name for a company account, the account holder's name otherwise)
    const contractRef = buildContractReference(
      type === 'company' ? formData.company_name : formData.account_holder_name
    );
    setFormData((prev) => ({ ...prev, contract_reference: contractRef }));
  };

  const validateForm = () => {
    const missingFields = [];
    const errors = [];
    
    // Check account holder type is selected
    if (!accountHolderType) {
      missingFields.push('Account Holder Type (Company or Natural Person)');
    }

    // Company-only required fields
    if (accountHolderType === 'company') {
      if (!formData.company_name.trim()) {
        missingFields.push('Company Name');
      } else if (hasSpecial(formData.company_name)) {
        errors.push(`Company Name: The CIPC does not allow any special characters in a company name. Please use letters, numbers and spaces only (not allowed: ${NOT_ALLOWED}).`);
      } else if (formData.company_name.trim().length < 4) {
        errors.push('Company Name: The company name must be more than 3 characters.');
      }
      if (!formData.company_registration_number.trim()) {
        missingFields.push('Company Registration Number');
      } else if (!CIPC_REG_NUMBER_RE.test(formData.company_registration_number.trim())) {
        errors.push('Company Registration Number: The registration number is invalid. Please enter it in the CIPC format YYYY/NNNNNN/NN (e.g. 2015/123456/07).');
      }
    }
    
    // Check all required fields with asterisks
    if (!formData.account_holder_name.trim()) {
      missingFields.push('Mandate given by (name of Accountholder)');
    }
    if (!formData.id_number.trim()) {
      missingFields.push('ID Number');
    }
    if (!formData.physical_address.trim()) {
      missingFields.push('Physical Address');
    }
    if (!formData.bank_name.trim()) {
      missingFields.push('Bank Name');
    }
    if (!formData.account_number.trim()) {
      missingFields.push('Account Number');
    }
    if (!formData.contact_number.trim()) {
      missingFields.push('Contact Number');
    }
    if (!formData.email.trim()) {
      missingFields.push('Email Address');
    }
    if (!formData.contract_reference.trim()) {
      missingFields.push('Contract Reference Number');
    }
    
    // Check for validation errors
    if (emailError) {
      errors.push(`Email Address: ${emailError}`);
    }
    if (contactNumberError) {
      errors.push(`Contact Number: ${contactNumberError}`);
    }
    if (idNumberError) {
      errors.push(`ID Number: ${idNumberError}`);
    }
    if (contractReferenceError) {
      errors.push(`Contract Reference: ${contractReferenceError}`);
    }
    if (companyContactPhoneError) {
      errors.push(`Contact Person Phone: ${companyContactPhoneError}`);
    }
    if (companyContactEmailError) {
      errors.push(`Contact Person Email: ${companyContactEmailError}`);
    }
    // Re-validate directly from formData (not just state) so this is caught even if the
    // field was never blurred/changed after being auto-filled or pasted.
    const physicalAddressValidationError = validatePhysicalAddress(formData.physical_address);
    if (physicalAddressValidationError) {
      errors.push(`Physical Address: ${physicalAddressValidationError}`);
    }
    
    // Check signature
    if (signatureRef.current?.isEmpty()) {
      missingFields.push('Account Holder Signature');
    }
    
    // Check acknowledgements - all 5 must be checked
    const uncheckedAgreements = [];
    if (!acknowledgements.agreement1) {
      uncheckedAgreements.push('Agreement 1 (Netcash authorization)');
    }
    if (!acknowledgements.agreement2) {
      uncheckedAgreements.push('Agreement 2 (Cancellation liability)');
    }
    if (!acknowledgements.agreement3) {
      uncheckedAgreements.push('Agreement 3 (Administrative surcharge)');
    }
    if (!acknowledgements.agreement4) {
      uncheckedAgreements.push('Agreement 4 (Variable mandate)');
    }
    if (!acknowledgements.agreement5) {
      uncheckedAgreements.push('Agreement 5 (Dishonoured debit order fee)');
    }
    if (accountHolderType === 'company' && !companyAuthChecked) {
      uncheckedAgreements.push('Authorised Representative Confirmation (Company)');
    }
    
    // Show comprehensive error message
    if (missingFields.length > 0 || errors.length > 0 || uncheckedAgreements.length > 0) {
      let errorMessage = '';
      
      if (missingFields.length > 0) {
        errorMessage += 'Please fill in the following required fields:\n\n';
        missingFields.forEach((field, index) => {
          errorMessage += `${index + 1}. ${field}\n`;
        });
      }
      
      if (errors.length > 0) {
        if (errorMessage) errorMessage += '\n';
        errorMessage += 'Please fix the following errors:\n\n';
        errors.forEach((error, index) => {
          errorMessage += `${index + 1}. ${error}\n`;
        });
      }
      
      if (uncheckedAgreements.length > 0) {
        if (errorMessage) errorMessage += '\n';
        errorMessage += 'Please accept the following acknowledgements:\n\n';
        uncheckedAgreements.forEach((agreement, index) => {
          errorMessage += `${index + 1}. ${agreement}\n`;
        });
      }
      
      // Show alert dialog with the error message
      alert(errorMessage);
      return false;
    }
    
    return true;
  };

  const handleFinalize = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setIsLoading(true);
      setShowSavingModal(true); // Show "Saving..." modal
      
      // Wait a moment to let the modal render before starting PDF generation
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Get signature as base64
      const signatureData = signatureRef.current.toDataURL();

      // Generate PDF for email
      let pdfBase64 = null;
      try {
        pdfBase64 = await generatePDF();
        console.log('PDF generated successfully');
      } catch (pdfError) {
        console.error('PDF generation error:', pdfError);
        toast.error('PDF generation failed. Please try again.');
        setShowSavingModal(false);
        setIsLoading(false);
        return;
      }

      // Save form data to database
      const response = await axios.post(`${API}/debit-order`, {
        ...formData,
        account_holder_type: accountHolderType,
        signature_data: signatureData,
      });

      setSavedFormId(response.data.id);
      console.log('Form data saved to database');
      
      // Send email with PDF to info@newredruthacademy.co.za
      try {
        await axios.post(`${API}/debit-order/send-email`, {
          form_id: response.data.id,
          pdf_data: pdfBase64,
          form_data: { ...formData, account_holder_type: accountHolderType },
        });
        console.log('Email sent to info@newredruthacademy.co.za');
      } catch (emailError) {
        console.error('Email sending error:', emailError);
        // Don't block the success modal if email fails
        toast.warning('PDF generated but email sending failed.');
      }
      
      // Hide saving modal and show success dialog
      setShowSavingModal(false);
      setShowEmailDialog(true);
      toast.success('Debit Order Mandate successfully signed!');
    } catch (error) {
      console.error('Error saving form:', error);
      setShowSavingModal(false); // Hide saving modal on error
      toast.error('Failed to save form. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const generatePDF = async () => {
    console.log('=== generatePDF called ===');
    try {
      console.log('Step 1: Getting form element...');
      const element = document.querySelector('.form-wrapper');
      if (!element) {
        throw new Error('Form wrapper not found');
      }
      console.log('Element found:', element.tagName);
      
      // Add a class to hide elements during capture
      console.log('Step 2: Adding generating-pdf class...');
      element.classList.add('generating-pdf');
      
      // Wait a bit for any transitions
      await new Promise(resolve => setTimeout(resolve, 200));

      // Get timestamp for signing
      const signedDateTime = new Date();
      const formattedDate = signedDateTime.toLocaleDateString('en-ZA', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      const formattedTime = signedDateTime.toLocaleTimeString('en-ZA', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
      const signedText = `Signed on: ${formattedDate} at ${formattedTime}`;

      // Configure html2pdf options with page breaks
      const options = {
        margin: [10, 10, 15, 10], // top, left, bottom, right margins in mm
        filename: `NewRedruthAcademy_Debit_Order_Mandate_${formData.account_holder_name.replace(/\s+/g, '_')}.pdf`,
        image: { type: 'jpeg', quality: 0.95 },
        html2canvas: { 
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff'
        },
        jsPDF: { 
          unit: 'mm', 
          format: 'a4', 
          orientation: 'portrait' 
        },
        pagebreak: { 
          mode: ['avoid-all', 'css', 'legacy'],
          before: '.page-break-before',
          after: '.page-break-after',
          avoid: ['.form-section', '.form-group', '.signature-section', '.acknowledgements-container']
        }
      };

      console.log('Step 3: Generating PDF with smart page breaks...');
      
      // Generate PDF using html2pdf
      const pdf = await html2pdf().set(options).from(element).toPdf().get('pdf');
      
      // Add page numbers and timestamp to each page
      const totalPages = pdf.internal.getNumberOfPages();
      for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
        pdf.setPage(pageNum);
        
        // Add page number at bottom center
        pdf.setFontSize(10);
        pdf.setTextColor(100, 100, 100);
        const pageText = `Page ${pageNum} of ${totalPages}`;
        const pageTextWidth = pdf.getTextWidth(pageText);
        pdf.text(pageText, (210 - pageTextWidth) / 2, 287); // 287mm from top (near bottom)
        
        // Add "Signed on" timestamp at bottom right
        pdf.setFontSize(9);
        pdf.setTextColor(80, 80, 80);
        const signedTextWidth = pdf.getTextWidth(signedText);
        pdf.text(signedText, 210 - 10 - signedTextWidth, 287);
      }

      // Remove the class
      element.classList.remove('generating-pdf');
      console.log('Step 4: Removed generating-pdf class');

      // Return the PDF as base64 for emailing (no download)
      console.log('Step 5: Converting PDF to base64 for email...');
      const pdfBase64 = pdf.output('dataurlstring');
      console.log('PDF converted to base64 for email');
      return pdfBase64;
      
    } catch (error) {
      console.error('=== PDF ERROR ===', error);
      console.error('Error details:', error.message, error.stack);
      toast.error('Failed to generate PDF: ' + error.message);
      throw error;
    } finally {
      // Always remove the class, even if generation threw, so real inputs
      // don't stay hidden on screen behind the PDF-only render divs.
      document.querySelector('.form-wrapper')?.classList.remove('generating-pdf');
    }
  };

  const handleThankYou = () => {
    // Close modal and refresh page
    setShowEmailDialog(false);
    window.location.reload();
  };

  // Builds the list shown on the success screen confirming exactly who received the
  // signed mandate copy - mirrors the same recipient logic used by the backend's
  // /api/debit-order/send-email CC gating (admin + account holder/representative + optional
  // company contact person email, only when valid).
  const emailRecipients = [
    { label: accountHolderType === 'company' ? 'Company Representative' : 'You', email: formData.email },
    { label: 'New Redruth Academy CC (Admin)', email: 'info@newredruthacademy.co.za' },
  ];
  if (accountHolderType === 'company' && formData.company_contact_email && !companyContactEmailError) {
    emailRecipients.push({ label: 'Company Contact Person', email: formData.company_contact_email });
  }

  return (
    <div className="app-container">
      <Toaster position="top-right" richColors />
      
      <div className="form-wrapper">
        <div className="form-header">
          <img src="/new-redruth-logo.png" alt="New Redruth Academy CC Logo" className="logo" />
          <h1 className="form-title">DEBIT ORDER MANDATE FORM</h1>
          <p className="form-subtitle">Electronic Authorization</p>
        </div>

        <div className="form-content">
          <div className="form-section account-type-section no-print" data-testid="account-holder-type-section">
            <label className="account-type-heading" data-testid="label-account-holder-type">
              Select Account Holder Type <span className="required-asterisk">*</span>
            </label>
            <p className="account-type-instruction">
              Please select an option below. All fields will remain disabled until you make a selection.
            </p>
            <div className="radio-group">
              <label className="radio-label">
                <input
                  data-testid="radio-account-holder-type-company"
                  type="radio"
                  name="account_holder_type_selector"
                  value="company"
                  checked={accountHolderType === 'company'}
                  onChange={() => handleAccountHolderTypeChange('company')}
                />
                <span>Company Bank Account</span>
              </label>
              <label className="radio-label">
                <input
                  data-testid="radio-account-holder-type-individual"
                  type="radio"
                  name="account_holder_type_selector"
                  value="individual"
                  checked={accountHolderType === 'individual'}
                  onChange={() => handleAccountHolderTypeChange('individual')}
                />
                <span>Natural Person Bank Account</span>
              </label>
            </div>
          </div>

          <div className={`form-body-gated ${!accountHolderType ? 'fields-disabled' : ''}`} data-testid="form-body-gated">
          <div className="form-section">
            <h2 className="section-title">A. AUTHORITY/MANDATE: ELECTRONIC</h2>

            {accountHolderType === 'company' && (
              <>
                <div className="form-group">
                  <label data-testid="label-company-name">Company Name <span className="required-asterisk">*</span></label>
                  <input
                    data-testid="input-company-name"
                    type="text"
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleInputChange}
                    onBlur={handleCompanyNameBlur}
                    placeholder="Enter registered company name, e.g. Apex Trading — (Pty) Ltd is added automatically"
                    className={(formData.company_name.length > 0 && (hasSpecial(formData.company_name) || formData.company_name.trim().length < 4)) ? 'input-error' : ''}
                    required
                  />
                  {formData.company_name.length > 0 && formData.company_name.trim().length < 4 && (
                    <span className="error-message" data-testid="company-name-length-error">
                      The company name must be more than 3 characters.
                    </span>
                  )}
                  {formData.company_name.length > 0 && hasSpecial(formData.company_name) && (
                    <span className="error-message" data-testid="company-name-special-char-error">
                      The CIPC does not allow any special characters in a company name. Please use letters, numbers and spaces only (not allowed: {NOT_ALLOWED}).
                    </span>
                  )}
                </div>

                <div className="form-group">
                  <label data-testid="label-company-registration-number">Company Registration Number <span className="required-asterisk">*</span></label>
                  <input
                    data-testid="input-company-registration-number"
                    type="text"
                    name="company_registration_number"
                    value={formData.company_registration_number}
                    onChange={handleInputChange}
                    placeholder="e.g. 2015/123456/07"
                    inputMode="numeric"
                    maxLength={14}
                    className={companyRegNumberStatus === 'invalid' ? 'input-error' : ''}
                    required
                  />
                  <p
                    className={`account-hint no-print${companyRegNumberStatus ? ` account-hint-${companyRegNumberStatus}` : ''}`}
                    data-testid="company-registration-number-hint"
                  >
                    {companyRegNumberStatus === 'valid'
                      ? 'Looks good — matches the standard CIPC format.'
                      : 'Expected CIPC format: YYYY/NNNNNN/NN (e.g. 2015/123456/07) — YYYY is the registration year, NNNNNN is a 6-digit sequence, and the last 2 digits indicate entity type (e.g. 07 = Private Company, 23 = Close Corporation).'}
                  </p>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label data-testid="label-company-contact-name">Company Contact Person (optional)</label>
                    <input
                      data-testid="input-company-contact-name"
                      type="text"
                      name="company_contact_name"
                      value={formData.company_contact_name}
                      onChange={handleInputChange}
                      placeholder="e.g. Accounts Department contact"
                    />
                  </div>

                  <div className="form-group">
                    <label data-testid="label-company-contact-phone">Contact Person Phone (optional)</label>
                    <input
                      data-testid="input-company-contact-phone"
                      type="tel"
                      name="company_contact_phone"
                      value={formData.company_contact_phone}
                      onChange={handleInputChange}
                      placeholder="e.g. 0821234567"
                      className={companyContactPhoneError ? 'input-error' : ''}
                      maxLength={10}
                    />
                    {companyContactPhoneError && (
                      <span className="error-message" data-testid="company-contact-phone-error">
                        {companyContactPhoneError}
                      </span>
                    )}
                    {!companyContactPhoneError && (
                      <p className={`account-hint no-print${formData.company_contact_phone.trim() ? ' account-hint-valid' : ''}`} data-testid="company-contact-phone-hint">
                        {formData.company_contact_phone.trim()
                          ? 'Looks good — valid South African cellphone format.'
                          : 'Expected format: 10 digits starting with 0 (e.g. 0821234567).'}
                      </p>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label data-testid="label-company-contact-email">Contact Person Email (optional)</label>
                  <input
                    data-testid="input-company-contact-email"
                    type="email"
                    name="company_contact_email"
                    value={formData.company_contact_email}
                    onChange={handleInputChange}
                    placeholder="e.g. accounts@company.co.za"
                    className={companyContactEmailError ? 'input-error' : ''}
                  />
                  {companyContactEmailError && (
                    <span className="error-message" data-testid="company-contact-email-error">
                      {companyContactEmailError}
                    </span>
                  )}
                  {!companyContactEmailError && (
                    <>
                      <p className={`account-hint no-print${formData.company_contact_email.trim() ? ' account-hint-valid' : ''}`} data-testid="company-contact-email-hint">
                        {formData.company_contact_email.trim()
                          ? 'Looks good — valid email format.'
                          : 'Expected format: a valid email address (e.g. accounts@company.co.za).'}
                      </p>
                      <p className="account-hint no-print" data-testid="company-contact-email-cc-notice">
                        If provided, this address will be copied (CC'd) on the signed mandate notification email sent to New Redruth Academy CC and the Company Representative.
                      </p>
                    </>
                  )}
                </div>

                <div className="acknowledgement-item company-auth-item">
                  <input
                    type="checkbox"
                    id="companyAuthConfirm"
                    data-testid="checkbox-company-authorization"
                    checked={companyAuthChecked}
                    onChange={() => setCompanyAuthChecked((prev) => !prev)}
                    className="acknowledgement-checkbox"
                  />
                  <label htmlFor="companyAuthConfirm" className="acknowledgement-label">
                    I confirm that I am duly authorised to represent and sign on behalf of the above-named company,
                    and I acknowledge that New Redruth Academy CC may rely on this mandate as validly authorised,
                    notwithstanding any internal restriction on my authority to bind the company (i.e. any ultra vires conduct).
                    {' '}<span className="required-asterisk">*</span>
                  </label>
                </div>

                <h3 className="company-rep-heading" data-testid="heading-company-representative">Company Representative</h3>
              </>
            )}

            <div className="form-group">
              <label data-testid="label-account-holder-name">Mandate given by (name of Accountholder) <span className="required-asterisk">*</span></label>
              <input
                data-testid="input-account-holder-name"
                type="text"
                name="account_holder_name"
                value={formData.account_holder_name}
                onChange={handleInputChange}
                placeholder="Enter full name"
                required
              />
            </div>

            <div className="form-group">
              <label data-testid="label-id-number">ID Number <span className="required-asterisk">*</span></label>
              <input
                data-testid="input-id-number"
                type="text"
                name="id_number"
                value={formData.id_number}
                onChange={handleInputChange}
                placeholder="Please enter your South African ID number"
                className={idNumberError ? 'input-error' : ''}
                required
                maxLength={13}
              />
              {idNumberError && (
                <span className="error-message" data-testid="id-number-error">
                  {idNumberError}
                </span>
              )}
              {!idNumberError && (
                <p className={`account-hint no-print${formData.id_number.trim() ? ' account-hint-valid' : ''}`} data-testid="id-number-hint">
                  {formData.id_number.trim()
                    ? 'Looks good — valid South African ID number.'
                    : 'Expected format: 13 digits (e.g. 9001015009086).'}
                </p>
              )}
            </div>

            <div className="form-group">
              <label data-testid="label-physical-address">Physical Address <span className="required-asterisk">*</span></label>
              <div className="address-field-container">
                <textarea
                  data-testid="input-physical-address"
                  name="physical_address"
                  value={formData.physical_address}
                  onChange={handleInputChange}
                  onFocus={() => setShowAddressTooltip(true)}
                  onBlur={() => setShowAddressTooltip(false)}
                  placeholder="Physical address line 01 (e.g. Unit 01)&#10;Physical address line 02 (e.g. Steyn City Estate)&#10;Physical address line 03 (e.g. 33 William Nicol Road)&#10;Physical address line 04 (e.g. Sandton)&#10;Physical address line 05 (e.g. 2196)"
                  rows="5"
                  className={`physical-address-textarea${physicalAddressError ? ' input-error' : ''}`}
                  required
                />
                {/* PDF-only rendering: html2canvas does not reliably preserve line breaks inside a
                    <textarea>, so we render each address line as its own block here and swap it in
                    (via the .generating-pdf CSS rule) right before PDF capture so the mandate PDF
                    always shows one address element per line, matching the guidance above. */}
                <div className="address-pdf-render" data-testid="address-pdf-render">
                  {formData.physical_address.split('\n').map((line, idx) => (
                    <div key={idx}>{line.trim() || '\u00A0'}</div>
                  ))}
                </div>
                {physicalAddressError && (
                  <span className="error-message" data-testid="physical-address-error">
                    {physicalAddressError}
                  </span>
                )}
                {showAddressTooltip && (
                  <div className="address-tooltip">
                    <div className="address-tooltip-header">Address Format Guide:</div>
                    <div className="address-tooltip-line">Line 01: Unit/Building number (e.g. Unit 01)</div>
                    <div className="address-tooltip-line">Line 02: Estate/Complex name (e.g. Steyn City Estate)</div>
                    <div className="address-tooltip-line">Line 03: Street address (e.g. 33 William Nicol Road)</div>
                    <div className="address-tooltip-line">Line 04: Suburb/City (e.g. Sandton)</div>
                    <div className="address-tooltip-line">Line 05: Postal code (e.g. 2196)</div>
                  </div>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label data-testid="label-bank-name">Bank Name (ABSA, FNB, Nedbank etc.) <span className="required-asterisk">*</span></label>
                <Popover open={openBankCombobox} onOpenChange={setOpenBankCombobox}>
                  <PopoverTrigger asChild>
                    <Button
                      data-testid="button-bank-selector"
                      variant="outline"
                      role="combobox"
                      aria-expanded={openBankCombobox}
                      className="bank-selector-button"
                    >
                      {formData.bank_name
                        ? SOUTH_AFRICAN_BANKS.find((bank) => bank.label === formData.bank_name)?.label
                        : "Select bank..."}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="popover-content-bank">
                    <Command>
                      <CommandInput placeholder="Search bank..." data-testid="input-search-bank" />
                      <CommandList>
                        <CommandEmpty>No bank found.</CommandEmpty>
                        <CommandGroup>
                          {SOUTH_AFRICAN_BANKS.map((bank) => (
                            <CommandItem
                              key={bank.value}
                              value={bank.label}
                              onSelect={(currentValue) => {
                                const selectedBank = SOUTH_AFRICAN_BANKS.find(
                                  (b) => b.label.toLowerCase() === currentValue.toLowerCase()
                                );
                                setFormData((prev) => ({
                                  ...prev,
                                  bank_name: currentValue === formData.bank_name ? '' : currentValue,
                                  branch_name: selectedBank ? 'UNIVERSAL BRANCH' : '',
                                  branch_code: selectedBank ? selectedBank.branchCode : '',
                                }));
                                // Validate account number with new bank selection
                                if (formData.account_number) {
                                  const warning = validateAccountNumber(formData.account_number, currentValue);
                                  setAccountNumberWarning(warning);
                                }
                                setOpenBankCombobox(false);
                              }}
                              data-testid={`bank-option-${bank.value}`}
                            >
                              <Check
                                className={cn(
                                  'mr-2 h-4 w-4',
                                  formData.bank_name === bank.label ? 'opacity-100' : 'opacity-0'
                                )}
                              />
                              {bank.label}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              <div className="form-group">
                <label data-testid="label-branch-name">Branch Name</label>
                <input
                  data-testid="input-branch-name"
                  type="text"
                  name="branch_name"
                  value={formData.branch_name}
                  onChange={handleInputChange}
                  placeholder=""
                  required
                  readOnly
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label data-testid="label-branch-code">Universal Bank Branch Code</label>
                <input
                  data-testid="input-branch-code"
                  type="text"
                  name="branch_code"
                  value={formData.branch_code}
                  onChange={handleInputChange}
                  readOnly
                />
              </div>

              <div className="form-group">
                <label data-testid="label-account-number">Account Number <span className="required-asterisk">*</span></label>
                <input
                  data-testid="input-account-number"
                  type="text"
                  name="account_number"
                  value={formData.account_number}
                  onChange={handleInputChange}
                  placeholder="Enter account number"
                  className={accountNumberWarning ? 'warning-input' : ''}
                  required
                />
                {accountNumberWarning && (
                  <p className="warning-message no-print">{accountNumberWarning}</p>
                )}
                {!accountNumberWarning && formData.bank_name && BANK_ACCOUNT_RULES[formData.bank_name] && (
                  <p className={`account-hint no-print${formData.account_number.trim() ? ' account-hint-valid' : ''}`} data-testid="account-number-hint">
                    {BANK_ACCOUNT_RULES[formData.bank_name].name}: {BANK_ACCOUNT_RULES[formData.bank_name].description}
                    {BANK_ACCOUNT_RULES[formData.bank_name].note ? ` — ${BANK_ACCOUNT_RULES[formData.bank_name].note}` : ''}
                  </p>
                )}
              </div>
            </div>

            <div className="form-group">
              <label data-testid="label-account-type">Type of Account <span className="required-asterisk">*</span></label>
              <div className="radio-group">
                <label className="radio-label">
                  <input
                    data-testid="radio-account-type-current"
                    type="radio"
                    name="account_type"
                    value="Current"
                    checked={formData.account_type === 'Current'}
                    onChange={handleInputChange}
                  />
                  <span>Current (aka Cheque)</span>
                </label>
                <label className="radio-label">
                  <input
                    data-testid="radio-account-type-savings"
                    type="radio"
                    name="account_type"
                    value="Savings"
                    checked={formData.account_type === 'Savings'}
                    onChange={handleInputChange}
                  />
                  <span>Savings</span>
                </label>
                <label className="radio-label">
                  <input
                    data-testid="radio-account-type-transmission"
                    type="radio"
                    name="account_type"
                    value="Transmission"
                    checked={formData.account_type === 'Transmission'}
                    onChange={handleInputChange}
                  />
                  <span>Transmission</span>
                </label>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label data-testid="label-signing-date">Date of Signing (Start Date) <span className="required-asterisk">*</span></label>
                <input
                  data-testid="input-signing-date"
                  type="date"
                  name="signing_date"
                  value={formData.signing_date}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label data-testid="label-contact-number">Contact Number (cellphone preferred) <span className="required-asterisk">*</span></label>
                <input
                  data-testid="input-contact-number"
                  type="tel"
                  name="contact_number"
                  value={formData.contact_number}
                  onChange={handleInputChange}
                  placeholder="e.g., 0821234567"
                  className={contactNumberError ? 'input-error' : ''}
                  maxLength={10}
                  required
                />
                {contactNumberError && (
                  <span className="error-message" data-testid="contact-number-error">
                    {contactNumberError}
                  </span>
                )}
                {!contactNumberError && (
                  <p className={`account-hint no-print${formData.contact_number.trim() ? ' account-hint-valid' : ''}`} data-testid="contact-number-hint">
                    {formData.contact_number.trim()
                      ? 'Looks good — valid South African cellphone format.'
                      : 'Expected format: 10 digits starting with 0 (e.g. 0821234567).'}
                  </p>
                )}
              </div>
            </div>

            <div className="form-group">
              <label data-testid="label-email">Email Address <span className="required-asterisk">*</span></label>
              <input
                data-testid="input-email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="e.g., yourname@example.com"
                className={emailError ? 'input-error' : ''}
                required
              />
              {emailError && (
                <span className="error-message" data-testid="email-error">
                  {emailError}
                </span>
              )}
              {!emailError && (
                <p className={`account-hint no-print${formData.email.trim() ? ' account-hint-valid' : ''}`} data-testid="email-hint">
                  {formData.email.trim()
                    ? 'Looks good — valid email format.'
                    : 'Expected format: a valid email address (e.g. yourname@example.com).'}
                </p>
              )}
            </div>

            <div className="form-group">
              <label data-testid="label-amount">Amount</label>
              <input
                data-testid="input-amount"
                type="text"
                name="amount"
                value={formData.amount}
                onChange={handleInputChange}
                placeholder=""
                required
                readOnly
              />
            </div>
          </div>

          <div className="form-section beneficiary-section">
            <h3 className="subsection-title">Beneficiary Information</h3>
            <div className="beneficiary-info">
              <p><strong>To (Name of Beneficiary):</strong> New Redruth Academy CC</p>
              <p><strong>Beneficiary Address:</strong> 1 Philip Engelbrecht Avenue, Meyersdal, Alberton</p>
              <p><strong>Abbreviated Shortname:</strong> NEWREDRUTH</p>
            </div>

            <div className="form-group">
              <label data-testid="label-contract-reference" className="contract-reference-label">
                Contract Reference Number (your account number with us) <span className="required-asterisk">*</span>
              </label>
              <input
                data-testid="input-contract-reference"
                type="text"
                name="contract_reference"
                value={formData.contract_reference}
                onChange={handleInputChange}
                placeholder="Enter contract reference number"
                className={contractReferenceError ? 'input-error' : (formData.contract_reference ? 'contract-reference-value' : '')}
                required
                maxLength={6}
              />
              <p className="contract-reference-helper">
                <strong>New clients:</strong> Leave unchanged. <strong>Existing clients:</strong> Update to your current account number.
              </p>
              {contractReferenceError && (
                <span className="error-message" data-testid="contract-reference-error">
                  {contractReferenceError}
                </span>
              )}
            </div>
          </div>

          <div className="form-section">
            <div className="authorization-text">
              <p>
                I hereby authorise Netcash (Pty) Ltd to issue and deliver payment instructions to your banker 
                for collection against my abovementioned account at my abovementioned bank on condition that 
                the sum of such payment instructions will not differ from my obligations as agreed to in the 
                Contract Reference Number.
              </p>
              <p>
                The individual payment instructions so authorised must be issued and delivered on the date when 
                the obligation in terms of the Agreement is due and the amount of each individual payment 
                instruction may not differ as agreed to in terms of the Agreement.
              </p>
              <p>
                The payment instructions so authorised to be issued must carry the Contract Reference Number, 
                included in the said payment instructions, and must be provided to identify the specific contract. 
                The said Contract Reference Number should be added to this form in section E before the issuing 
                of any payment instruction and communicated directly after having been completed.
              </p>
              <p>
                I agree that the first payment instruction will be issued and delivered on invoice date and 
                thereafter regularly on any subsequent invoice dates.
              </p>
              <p>
                If however, the date of the payment instruction falls on a non-processing day (weekend or public 
                holiday) I agree that the payment instruction may be debited against my account on the following 
                business day; or
              </p>
              <p>
                Subsequent payment instructions will continue to be delivered in terms of this authority until 
                the obligations in terms of the Agreement have been paid or until this authority is cancelled by 
                me by giving you notice in writing of not less than 7 working days and sent by prepaid registered 
                post, delivered to your address indicated above, or per email to <strong><u>info@newredruthacademy.co.za</u></strong>. I agree 
                all dishonoured/unpaid/bounced debit orders carry a R50 admin fee.
              </p>
            </div>
          </div>

          <div className="form-section">
            <h2 className="section-title">B. MANDATE</h2>
            <p className="mandate-text">
              I acknowledge that all payment instructions issued by you will be treated by my abovementioned 
              bank as if the instructions had been issued by me personally.
            </p>
          </div>

          <div className="form-section">
            <h2 className="section-title">C. CANCELLATION</h2>
            <p className="mandate-text">
              I agree that although this authority and mandate may be cancelled by me, such cancellation 
              will not cancel the Agreement. I also understand that I cannot reclaim amounts, which have 
              been withdrawn from my account (paid) in terms of this authority and mandate if such amounts 
              were legally owing by me.
            </p>
          </div>

          <div className="form-section">
            <h2 className="section-title">D. ASSIGNMENT</h2>
            <p className="mandate-text">
              I acknowledge that this authority may be ceded or assigned to a third party if the Agreement 
              is also ceded or assigned to that third party.
            </p>
          </div>

          <div className="form-section">
            <h2 className="section-title">E. AGREEMENT REFERENCE NUMBER</h2>
            <label className="agreement-reference-label">The agreement reference number is</label>
            <input
              data-testid="input-agreement-reference"
              type="text"
              name="agreement_reference"
              value={formData.contract_reference}
              className="agreement-reference-field"
              readOnly
            />
            <div className="agreement-reference-pdf-render" data-testid="agreement-reference-pdf-render">
              {formData.contract_reference}
            </div>
          </div>

          <div className="form-section">
            <h2 className="section-title">F. RECORD OF CLIENT ACKNOWLEDGEMENTS</h2>
            <div className="acknowledgements-container">
              <div className="acknowledgement-item">
                <input
                  type="checkbox"
                  id="agreement1"
                  data-testid="checkbox-agreement1"
                  checked={acknowledgements.agreement1}
                  onChange={() => handleAcknowledgementChange('agreement1')}
                  className="acknowledgement-checkbox"
                />
                <label htmlFor="agreement1" className="acknowledgement-label">I accept this debit order mandate and authorise Netcash (Pty) Ltd to issue and deliver payment instructions to my bank.</label>
              </div>
              <div className="acknowledgement-item">
                <input
                  type="checkbox"
                  id="agreement2"
                  data-testid="checkbox-agreement2"
                  checked={acknowledgements.agreement2}
                  onChange={() => handleAcknowledgementChange('agreement2')}
                  className="acknowledgement-checkbox"
                />
                <label htmlFor="agreement2" className="acknowledgement-label">I understand that cancelling or revoking this mandate does not legally relieve me of any liability towards New Redruth Academy CC, nor does it cancel any existing agreement with New Redruth Academy CC.</label>
              </div>
              <div className="acknowledgement-item">
                <input
                  type="checkbox"
                  id="agreement3"
                  data-testid="checkbox-agreement3"
                  checked={acknowledgements.agreement3}
                  onChange={() => handleAcknowledgementChange('agreement3')}
                  className="acknowledgement-checkbox"
                />
                <label htmlFor="agreement3" className="acknowledgement-label">I understand that cancelling or revoking this mandate results in an administrative surcharge of R150.00 on every invoice.</label>
              </div>
              <div className="acknowledgement-item">
                <input
                  type="checkbox"
                  id="agreement4"
                  data-testid="checkbox-agreement4"
                  checked={acknowledgements.agreement4}
                  onChange={() => handleAcknowledgementChange('agreement4')}
                  className="acknowledgement-checkbox"
                />
                <label htmlFor="agreement4" className="acknowledgement-label">I understand that this is a variable mandate, meaning the amount may vary due to additional services requested by me or due to price increases.</label>
              </div>
              <div className="acknowledgement-item">
                <input
                  type="checkbox"
                  id="agreement5"
                  data-testid="checkbox-agreement5"
                  checked={acknowledgements.agreement5}
                  onChange={() => handleAcknowledgementChange('agreement5')}
                  className="acknowledgement-checkbox"
                />
                <label htmlFor="agreement5" className="acknowledgement-label">I understand that an administration fee of R50.00 applies to every dishonoured or returned debit order.</label>
              </div>
            </div>
          </div>

          <div className="form-section signature-section">
            <h3 className="subsection-title">Account Holder Signature</h3>
            <p className="signature-instruction">Please draw your signature in the box below:</p>
            
            <div className="signature-container">
              <SignatureCanvas
                ref={signatureRef}
                canvasProps={{
                  className: 'signature-canvas',
                  'data-testid': 'signature-canvas',
                }}
              />
            </div>
            
            <p className="signature-account-text">SIGNATURE AS USED FOR OPERATING ON THE ACCOUNT</p>
            
            <button
              data-testid="button-clear-signature"
              type="button"
              onClick={clearSignature}
              className="clear-signature-btn no-print"
            >
              Clear Signature
            </button>
          </div>

          <div className="form-actions no-print">
            <button
              data-testid="button-finalize"
              onClick={handleFinalize}
              disabled={isLoading}
              className="finalize-btn"
            >
              {isLoading ? 'Processing...' : 'Finalise & Send'}
            </button>
          </div>
          </div>

          <div className="letterhead-footer-container">
            <img src="/letterhead-footer.jpg" alt="New Redruth Academy CC Footer" className="letterhead-footer" />
          </div>
        </div>
      </div>

      {/* Saving Modal */}
      {showSavingModal && (
        <div className="modal-overlay" data-testid="saving-modal">
          <div className="modal-content saving-modal-content">
            <div className="spinner"></div>
            <h2>Saving...</h2>
            <p className="modal-description">
              Please wait while we generate your signed PDF document.
            </p>
          </div>
        </div>
      )}

      {/* Success Dialog */}
      {showEmailDialog && (
        <div className="modal-overlay" data-testid="success-dialog">
          <div className="modal-content success-modal">
            <h2>Debit Order Mandate Successfully Signed</h2>
            <p className="modal-description success-message">
              Thank you for signing the Debit Order Mandate Form, a signed PDF copy has been sent for processing.
            </p>

            <div className="email-recipients-confirmation" data-testid="email-recipients-confirmation">
              <p className="email-recipients-heading">A copy of the signed mandate was sent to:</p>
              {emailRecipients.map((recipient, idx) => (
                <div key={idx} className="email-recipient-item" data-testid={`email-recipient-${idx}`}>
                  <Check className="email-recipient-check" />
                  <span>{recipient.label}: <strong>{recipient.email}</strong></span>
                </div>
              ))}
            </div>

            <p className="modal-description success-message">
              <small>You may close this tab/screen on your browser now.</small>
            </p>

            <div className="modal-actions">
              <button
                data-testid="button-thank-you"
                onClick={handleThankYou}
                className="send-btn thank-you-btn"
              >
                Thank You!
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
